import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useQueryCache } from 'react-query';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import { useLoggedInUser } from 'api';

import { offersActivate, offersDeactivate } from 'api/offerApi';

import { isRecruiterUser } from 'auth/token';

const useToggleOfferActive = (
	isActive: boolean,
	id: string,
	validTo?: string,
) => {
	const cache = useQueryCache();
	const user = useLoggedInUser();
	const { university } = useTheme();

	const deactivation_consequence =
		university === 'MUNI'
			? 'deactivation_consequence_muni'
			: 'deactivation_consequence';

	const { t } = useTranslation('hooks/use_toggle_offer_active/');

	return useCallback(async () => {
		if (
			(isActive &&
				!window.confirm(
					`${t('deactivate_question')} ${
						isRecruiterUser(user) ? t(deactivation_consequence) : ''
					}`,
				)) ||
			!user
		) {
			return;
		}
		const response = await (isActive
			? offersDeactivate(id, user.personType)
			: offersActivate(id, user.personType, validTo));

		if (!response) {
			toast.error(
				`${t('offer_not')} ${isActive ? t('deactivate') : t('activate')}`,
			);
			return;
		}

		await cache.invalidateQueries(['active-license']);
		await cache.invalidateQueries(['offers', 'logged-in']);
		await cache.invalidateQueries(['offer', id]);
		await cache.invalidateQueries(['admin', 'offers']);
		toast.success(
			`${t('offer')} ${isActive ? t('deactivated') : t('activated')}`,
		);
	}, [isActive, t, user, id, cache, deactivation_consequence, validTo]);
};

export default useToggleOfferActive;
